import PublicHeader from '../components/PublicHeader';
import SiteFooter from '../components/SiteFooter';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl font-bold text-white mb-1"><span role="img" aria-label="lock">🔐</span> Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-10">Effective: March 24, 2026</p>

          <div className="space-y-8 text-gray-400 leading-relaxed">

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">1. Information We Collect</h2>

              <h3 className="font-semibold text-gray-200 mb-2">1.1 Twitch Account</h3>
              <p className="mb-3">When you log in with Twitch OAuth, we receive and store the following from Twitch:</p>
              <ul className="list-disc list-inside mb-3 space-y-1 pl-2">
                <li>Twitch user ID — to uniquely identify your account</li>
                <li>Display name and login — to personalise your experience</li>
                <li>Profile picture URL — displayed in the interface</li>
                <li>Email address — collected via the <code className="text-primary text-sm">user:read:email</code> scope, used for account communications</li>
              </ul>
              <p>We do not collect your Twitch password.</p>

              <h3 className="font-semibold text-gray-200 mb-2 mt-5">1.2 Clip Metadata</h3>
              <p>When you import clips from Twitch channels, we store metadata about those clips (title, URL, thumbnail, broadcaster name, creation date, view counts). This data originates from the Twitch API and is subject to Twitch's own terms and privacy policy.</p>

              <h3 className="font-semibold text-gray-200 mb-2 mt-5">1.3 Collections and Exports</h3>
              <p>We store the collections, settings, and HTML exports you create within the Service, along with associated tags, filters, and preferences you configure.</p>

              <h3 className="font-semibold text-gray-200 mb-2 mt-5">1.4 Payment Information</h3>
              <p>Payments are processed by Stripe. We do not store your payment card details. We receive and store a Stripe customer ID, subscription status, plan tier, and transaction history to manage your subscription. Stripe's own privacy policy governs how Stripe handles your payment data.</p>

              <h3 className="font-semibold text-gray-200 mb-2 mt-5">1.5 Waiting List</h3>
              <p>If you submit your email address to join the waiting list, we store it to notify you when access becomes available.</p>

              <h3 className="font-semibold text-gray-200 mb-2 mt-5">1.6 Analytics</h3>
              <p>We use Google Analytics 4 (GA4) to collect anonymised, aggregated usage data — including pages visited, session duration, device type, and referring URLs — to improve the Service. GA4 may set cookies on your device. You can opt out via your browser settings.</p>

              <h3 className="font-semibold text-gray-200 mb-2 mt-5">1.7 Technical Data</h3>
              <p>We automatically collect your IP address, browser type and version, operating system, and request timestamps for security monitoring and operational diagnostics.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Authenticate you and maintain your session</li>
                <li>Provide, operate, and improve the Service</li>
                <li>Manage your subscription and billing</li>
                <li>Send transactional emails (account activity, export completion, subscription events)</li>
                <li>Respond to support requests</li>
                <li>Detect and prevent fraud, abuse, or security incidents</li>
                <li>Comply with legal obligations</li>
              </ul>
              <p className="mt-3">We do not sell your personal data to third parties. We do not use your data for advertising outside of aggregate analytics.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">3. Cookies and Local Storage</h2>
              <p className="mb-3">We use a JWT session cookie scoped to <code className="text-primary text-sm">.clipto.tv</code>, marked <code className="text-primary text-sm">HttpOnly</code> and <code className="text-primary text-sm">Secure</code>. You can clear it by logging out. We also use Google Analytics cookies as described above.</p>
              <p>Most browsers allow you to refuse or delete cookies. Disabling the session cookie will prevent you from using authenticated parts of the Service.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">4. Third-Party Services</h2>
              <p className="mb-4">We share data with the following third parties to operate the Service:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-dark-600 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-dark-700 text-gray-300">
                      <th className="text-left px-4 py-3 font-semibold">Service</th>
                      <th className="text-left px-4 py-3 font-semibold">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-600">
                    <tr className="bg-dark-800/50">
                      <td className="px-4 py-3 text-gray-200">Twitch</td>
                      <td className="px-4 py-3">Authentication, clip data</td>
                    </tr>
                    <tr className="bg-dark-800/50">
                      <td className="px-4 py-3 text-gray-200">Stripe</td>
                      <td className="px-4 py-3">Payment processing</td>
                    </tr>
                    <tr className="bg-dark-800/50">
                      <td className="px-4 py-3 text-gray-200">Google Analytics 4</td>
                      <td className="px-4 py-3">Usage analytics</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-3">Each of these services operates under its own privacy policy.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">5. Data Retention</h2>
              <ul className="space-y-2 pl-2">
                <li><span className="text-gray-200 font-medium">Account data:</span> Retained while your account is active. Deleted within 30 days of account deletion, except where legally required.</li>
                <li><span className="text-gray-200 font-medium">Clip metadata:</span> Retained until you delete your collection or account.</li>
                <li><span className="text-gray-200 font-medium">Export files:</span> Available for a limited time after generation (typically 1 hour), then deleted from our servers.</li>
                <li><span className="text-gray-200 font-medium">Payment records:</span> Retained as required by applicable law (typically 7 years).</li>
                <li><span className="text-gray-200 font-medium">Waiting list emails:</span> We store the email you provide to notify you when access becomes available. You may request deletion at any time.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights</h2>
              <p className="mb-3">Depending on your location, you may have the right to access, correct, delete, or export your personal data, and to object to or restrict certain processing. Where processing is based on consent, you may withdraw it at any time.</p>
              <p>To exercise any of these rights, <Link to="/contact" className="text-primary hover:underline">contact us</Link>. We will respond within 30 days.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">7. Data Security</h2>
              <p className="mb-3">We use HTTPS/TLS for all data in transit, <code className="text-primary text-sm">HttpOnly</code> session cookies, environment-variable–based secret management, and Stripe webhook signature verification.</p>
              <p>Despite these measures, no system is completely secure. We cannot guarantee absolute security of your data.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">8. Children's Privacy</h2>
              <p>The Service is not directed to children under 13 (or 16 in the EU). We do not knowingly collect data from children. If you believe a child has provided us with personal data, <Link to="/contact" className="text-primary hover:underline">contact us</Link> immediately and we will delete it.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">9. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. When we do, we will update the date at the top. For material changes, we will notify you via your account email or via a notice on the Service.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">10. Contact</h2>
              <p>For questions or concerns about this Privacy Policy or our data practices, <Link to="/contact" className="text-primary hover:underline">visit our Contact page</Link>.</p>
            </section>

          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
